import React, { useRef, useState, useMemo } from 'react';
import { Box, Tooltip } from '@mui/material';
import moment from 'moment';
import { isClosedByMinCount } from './classClosed';
import {
  CalendarContainer,
  CalendarTable,
  TableHeaderCell,
  DayLabel,
  DateLabel,
  TimeCell,
  EventCell,
  RowDivider,
  EventCard,
  EventCardHeader,
  EventCardTitle,
  EventCardTime,
  EventCardTrainer,
  EventCardBadge,
  EventCardBadgeText,
  EventCardHorizontal,
  EventCardHorizontalContent,
  EventCardHorizontalTitle,
  EventCardHorizontalInfo,
  EventCardHorizontalTimeRow,
  EventCardHorizontalCategory,
  EventCardHorizontalBadge,
  EventCardHorizontalBadgeText,
  CurrentTimeLine,
  CurrentTimeDot,
  TooltipLessonHeader,
  TooltipLessonTitle,
  TooltipStatusBadge,
  TooltipBody,
  TooltipInfoRow,
  TooltipInfoLabel,
  TooltipInfoValue,
  TodayBadge,
} from './GroupLessonStyled';
import { MIN_CARD_EXPOSED_HEIGHT } from '../privateLesson/PrivateLessonStyled';
import { layoutStackedOffsets } from '../privateLesson/eventOverlapLayout';

// moment().day() 순서(일=0) — 주 시작 요일이 바뀌어도 날짜의 요일로 찾는다
const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const WEEKDAYS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatClassLabel = eventData => {
  return eventData.className || '';
};

const MINUTES_PER_HOUR = 60;

const GroupLessonCalendar = ({
  hours, // 행 목록 — { label, startMinutes(자정부터 분), minutes(행 길이) }. 마지막 행은 표 칸보다 짧을 수 있다
  currentWeekStart,
  periodTimes,
  allEvents,
  selectedStatusFilter,
  mainLang,
  cellHeight = 80, // 1시간 픽셀 (줌 상태). 행 높이는 행 길이에 비례 — 시간→px 는 칸 단위와 무관하게 이 값 기준
  timeUnitMinutes = 60, // 표 칸 단위(분) — 그룹 스케줄 설정(30/60/120)
  snapMinutes = 60, // 선택 단위(분) — 빈칸 hover·클릭·우클릭 시작 시각 간격
  cardMinMinutes = 0, // 짧은 수업 카드의 최소 높이(분)
  onCellClick,
  onCellContextMenu,
  onEventClick,
  onEventContextMenu,
  isMenuOpen,
  highlightedIds = [],
  onClearHighlight,
}) => {
  const tableRef = useRef();
  const hasDraggedRef = useRef(false);
  const cardMinHeightPx = (cardMinMinutes / MINUTES_PER_HOUR) * cellHeight;
  // 표 마지막 시각(자정부터 분) — 선택 구간이 이 뒤로 넘지 않게 자른다
  const lastRow = hours[hours.length - 1];
  const rangeEndMinutes = lastRow ? lastRow.startMinutes + lastRow.minutes : 24 * MINUTES_PER_HOUR;

  // 셀 안 마우스 위치 → 그 위치가 속한 선택 단위 구간의 시작 시각(분). 셀 테두리 픽셀에서 옆 행으로 넘어가지 않게 행 안으로 자른다.
  const snapStartAt = (e, row) => {
    const offsetY = e.clientY - e.currentTarget.getBoundingClientRect().top;
    const within = Math.min(Math.max(Math.floor((offsetY / cellHeight) * MINUTES_PER_HOUR), 0), row.minutes - 1);
    return Math.floor((row.startMinutes + within) / snapMinutes) * snapMinutes;
  };

  // 빈칸 hover 박스를 선택 단위 구간에 맞춘다 — 리렌더 없이 셀 CSS 변수만 바꾼다(EventCell ::after).
  // 구간이 행과 정확히 같으면 변수를 지워 기존 모양(셀 전체) 그대로 둔다.
  const updateHoverBox = (e, row) => {
    const cell = e.currentTarget;
    const start = snapStartAt(e, row);
    const length = Math.min(snapMinutes, rangeEndMinutes - start);
    if (start === row.startMinutes && length === row.minutes) {
      cell.style.removeProperty('--hover-top');
      cell.style.removeProperty('--hover-height');
      return;
    }
    cell.style.setProperty('--hover-top', `${((start - row.startMinutes) / MINUTES_PER_HOUR) * cellHeight}px`);
    cell.style.setProperty('--hover-height', `${(length / MINUTES_PER_HOUR) * cellHeight}px`);
  };

  // hover 중인 이벤트 키 (Tooltip을 controlled로 제어 — 겹친 카드에서 잔존 방지)
  const [hoveredKey, setHoveredKey] = useState(null);

  // 드래그 스크롤 상태
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // 주간 날짜 배열 생성
  const getWeekDates = () => {
    return Array.from({ length: 7 }, (_, i) => currentWeekStart.clone().add(i, 'days'));
  };

  // 드래그 스크롤 핸들러
  const handleMouseDown = e => {
    if (e.target.closest('.event-card') || e.target.closest('button')) {
      return;
    }
    setIsDragging(true);
    setStartX(e.pageX - tableRef.current.offsetLeft);
    setScrollLeft(tableRef.current.scrollLeft);
    hasDraggedRef.current = false;
    tableRef.current.style.cursor = 'grabbing';
  };

  const handleMouseLeave = () => {
    if (isDragging) {
      setIsDragging(false);
      tableRef.current.style.cursor = 'grab';
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (tableRef.current) {
      tableRef.current.style.cursor = 'grab';
    }
  };

  const handleMouseMove = e => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - tableRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    if (Math.abs(walk) > 5) {
      hasDraggedRef.current = true;
    }
    tableRef.current.scrollLeft = scrollLeft - walk;
  };

  // 시간 문자열을 인덱스로 변환
  const timeToIndex = timeString => {
    const [hour, minute] = timeString.split(':').map(Number);
    return hour + minute / 60;
  };

  // 값 제한 함수
  const clamp = (value, min, max) => Math.max(min, Math.min(value, max));

  // 시간이 겹치는지 확인하는 함수
  const isTimeOverlapping = (start1, end1, start2, end2) => {
    const [h1, m1] = start1.split(':').map(Number);
    const [h2, m2] = end1.split(':').map(Number);
    const [h3, m3] = start2.split(':').map(Number);
    const [h4, m4] = end2.split(':').map(Number);

    const start1Minutes = h1 * 60 + m1;
    const end1Minutes = h2 * 60 + m2;
    const start2Minutes = h3 * 60 + m3;
    const end2Minutes = h4 * 60 + m4;

    if (start1Minutes === start2Minutes && end1Minutes === end2Minutes) {
      return true;
    }

    return start1Minutes < end2Minutes && start2Minutes < end1Minutes;
  };

  // 날짜별로 이벤트에 열(column) 할당
  const eventGroupMap = useMemo(() => {
    const map = new Map();

    if (!allEvents || allEvents.length === 0) return map;

    const eventsByDate = new Map();
    allEvents.forEach(event => {
      const eventData = event?.extendedProps?.eventData;
      if (!eventData) return;
      const date = eventData.classDate;
      if (!eventsByDate.has(date)) {
        eventsByDate.set(date, []);
      }
      eventsByDate.get(date).push(event);
    });

    eventsByDate.forEach((events, date) => {
      events.sort((a, b) => {
        const aData = a?.extendedProps?.eventData;
        const bData = b?.extendedProps?.eventData;
        if (!aData || !bData) return 0;
        const [aH, aM] = aData.startTime.split(':').map(Number);
        const [bH, bM] = bData.startTime.split(':').map(Number);
        return aH * 60 + aM - (bH * 60 + bM);
      });

      // Union-Find로 연결된 겹침 그룹 찾기
      const parent = new Map();
      events.forEach(event => parent.set(event, event));

      const find = event => {
        if (parent.get(event) !== event) {
          parent.set(event, find(parent.get(event)));
        }
        return parent.get(event);
      };

      const union = (event1, event2) => {
        const root1 = find(event1);
        const root2 = find(event2);
        if (root1 !== root2) {
          parent.set(root1, root2);
        }
      };

      // 겹치는 이벤트들을 같은 그룹으로 묶음
      for (let i = 0; i < events.length; i++) {
        const eventDataI = events[i]?.extendedProps?.eventData;
        if (!eventDataI) continue;

        for (let j = i + 1; j < events.length; j++) {
          const eventDataJ = events[j]?.extendedProps?.eventData;
          if (!eventDataJ) continue;

          if (isTimeOverlapping(eventDataI.startTime, eventDataI.endTime, eventDataJ.startTime, eventDataJ.endTime)) {
            union(events[i], events[j]);
          }
        }
      }

      // 그룹별로 이벤트 모으기
      const groups = new Map();
      events.forEach(event => {
        const root = find(event);
        if (!groups.has(root)) {
          groups.set(root, []);
        }
        groups.get(root).push(event);
      });

      // 각 그룹 내에서 열 할당 (겹치는 수업은 좌우로 나란히 배치)
      groups.forEach(groupEvents => {
        // 그룹 내 이벤트들을 시작 시간순으로 정렬
        groupEvents.sort((a, b) => {
          const aData = a?.extendedProps?.eventData;
          const bData = b?.extendedProps?.eventData;
          if (!aData || !bData) return 0;
          const [aH, aM] = aData.startTime.split(':').map(Number);
          const [bH, bM] = bData.startTime.split(':').map(Number);
          return aH * 60 + aM - (bH * 60 + bM);
        });

        // 최대 10개 열 지원
        const colEnds = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        const eventColumnMap = new Map();
        let maxColumnUsed = -1;

        // 열 할당
        groupEvents.forEach(event => {
          const eventData = event?.extendedProps?.eventData;
          if (!eventData) return;

          const [startH, startM] = eventData.startTime.split(':').map(Number);
          const [endH, endM] = eventData.endTime.split(':').map(Number);
          const eventStart = startH * 60 + startM;
          const eventEnd = endH * 60 + endM;

          let column = -1;
          for (let i = 0; i < 10; i++) {
            if (colEnds[i] <= eventStart) {
              column = i;
              colEnds[i] = eventEnd;
              if (i > maxColumnUsed) maxColumnUsed = i;
              break;
            }
          }

          eventColumnMap.set(event, column);
        });

        // totalColumns 계산
        const totalColumns = maxColumnUsed + 1;
        const hasOverlap = groupEvents.length > 1;

        // 결과 저장
        groupEvents.forEach(event => {
          const column = eventColumnMap.get(event);
          map.set(event, {
            column,
            hasOverlap,
            totalColumns,
          });
        });
      });
    });

    return map;
  }, [allEvents]);

  // 카드 최소 높이 때문에 연달아 있는 짧은 수업이 앞 카드를 덮을 때, 앞 카드 시간 줄(18px)은 보이도록 뒤 카드를 민다.
  // 지점 스케줄과 같은 규칙(layoutStackedOffsets). 시작 간격이 18px 이상이면(1시간 이상 수업만 있는 날 등) 0 이다.
  const stackOffsets = useMemo(() => {
    const result = new Map();
    const byDate = new Map();
    (allEvents || []).forEach(event => {
      const eventData = event?.extendedProps?.eventData;
      if (!eventData?.startTime || !eventData?.endTime) return;
      const [startHour, startMinute] = eventData.startTime.split(':').map(Number);
      const [endHour, endMinute] = eventData.endTime.split(':').map(Number);
      if (!byDate.has(eventData.classDate)) byDate.set(eventData.classDate, []);
      byDate.get(eventData.classDate).push({ event, startHour, startMinute, endHour, endMinute });
    });
    byDate.forEach(items => {
      layoutStackedOffsets(items, cellHeight, MIN_CARD_EXPOSED_HEIGHT).forEach((offset, item) => {
        if (offset > 0) result.set(item.event, offset);
      });
    });
    return result;
  }, [allEvents, cellHeight]);

  // 그리는 순서 = 시작 시각 순 — 같은 z-index 끼리는 늦게 시작한 카드가 위에 온다(지점 스케줄과 같은 규칙)
  const startOrderedEvents = useMemo(
    () =>
      (allEvents || [])
        .map((event, idx) => ({ event, idx }))
        .sort((a, b) =>
          (a.event?.extendedProps?.eventData?.startTime || '').localeCompare(
            b.event?.extendedProps?.eventData?.startTime || '',
          ),
        ),
    [allEvents],
  );

  // 날짜별 최대 열 수 계산 (셀 너비 결정용)
  const maxColumnsByDate = useMemo(() => {
    const result = new Map();

    if (!allEvents || allEvents.length === 0) return result;

    allEvents.forEach(event => {
      const groupInfo = eventGroupMap.get(event);
      if (!groupInfo) return;

      const eventData = event?.extendedProps?.eventData;
      if (!eventData) return;

      const date = eventData.classDate;
      const currentMax = result.get(date) || 1;
      if (groupInfo.totalColumns > currentMax) {
        result.set(date, groupInfo.totalColumns);
      }
    });

    return result;
  }, [allEvents, eventGroupMap]);

  // 날짜별 셀 너비 계산 (5개 이상: 300px, 0~4개: 250px)
  const getCellWidth = dateStr => {
    const maxColumns = maxColumnsByDate.get(dateStr) || 0;
    return maxColumns >= 5 ? 300 : 250;
  };

  // 시간/공개 여부 무관하게 예약 인원만으로 상태 판별 (필터용)
  const getReservationStatus = eventData => {
    const { reservedMemberCount = 0, maxMemberCount = 0, attendedMemberCount = 0 } = eventData;
    const totalMembers = reservedMemberCount + attendedMemberCount;
    if (totalMembers >= maxMemberCount) return 'full';
    if (totalMembers === 0) return 'none';
    return 'under';
  };

  // 상태에 따른 색상 함수들
  const getEventStatus = eventData => {
    const now = moment();
    const isPast = now.isAfter(moment(`${eventData.classDate} ${eventData.endTime}`, 'YYYY-MM-DD HH:mm'));
    const { openYn, closeDate, reservedMemberCount, maxMemberCount, attendedMemberCount, absentedMemberCount } = eventData;
    // 폐강은 지난 수업이어도 폐강으로 보여준다(예약했던 회원에게 연락해야 할 수 있음)
    if (isClosedByMinCount(eventData)) return 'canceledByCount';
    if (isPast) return 'past';
    if (openYn === 'N') return 'closed';
    if (reservedMemberCount + attendedMemberCount >= maxMemberCount) return 'full';
    if (reservedMemberCount === 0 && attendedMemberCount === 0 && absentedMemberCount === 0) return 'none';
    return 'under';
  };

  const STATUS_COLORS = {
    past:   { card: '#D9D9D9', badge: '#BFBFBF', font: '#404040', label: '종료' },
    closed: { card: '#EDEDED', badge: '#D1D1D1', font: '#404040', label: '비공개' },
    canceledByCount: { card: '#EDEDED', badge: '#5C5C5C', font: '#FFFFFF', label: '폐강' },
    full:   { card: '#DDF4E6', badge: '#A7E3BE', font: '#247E46', label: '예약마감' },
    none:   { card: '#FCE3DE', badge: '#F6B6A9', font: '#9E230B', label: '예약없음' },
    under:  { card: '#FFF0DE', badge: '#FFD9A9', font: '#AA6000', label: '예약미달' },
  };

  const getStatusColor = eventData => STATUS_COLORS[getEventStatus(eventData)].card;
  const getStatusFontBackgroundColor = eventData => STATUS_COLORS[getEventStatus(eventData)].badge;
  const getStatusFontColor = eventData => STATUS_COLORS[getEventStatus(eventData)].font;
  const getStatusLabel = eventData => STATUS_COLORS[getEventStatus(eventData)].label;

  // 수업 시간 길이 계산 (분 단위)
  const calculateDuration = (startTime, endTime) => {
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return endMinutes - startMinutes;
  };

  // 현재 시간 표시선 계산 — 지금 시각이 이 행 안일 때만
  const getCurrentTimeIndicator = (row, dayIndex) => {
    const now = moment();
    const cellDate = currentWeekStart.clone().add(dayIndex, 'days');
    const nowMinutes = now.hour() * MINUTES_PER_HOUR + now.minute();

    if (!cellDate.isSame(now, 'day') || nowMinutes < row.startMinutes || nowMinutes >= row.startMinutes + row.minutes) {
      return null;
    }

    const topPosition = ((nowMinutes - row.startMinutes) / MINUTES_PER_HOUR) * cellHeight;

    return (
      <CurrentTimeLine topPosition={topPosition}>
        <CurrentTimeDot />
      </CurrentTimeLine>
    );
  };

  // 이벤트 렌더링 — 수업은 시작 시각이 속한 행에만 그린다. 위치·높이 퍼센트는 행 높이 기준
  const renderEvents = (row, dayIndex) => {
    const hour = row.startMinutes / MINUTES_PER_HOUR; // 행 시작(시, 30분 칸이면 9.5 처럼 소수)
    const rowHours = row.minutes / MINUTES_PER_HOUR;
    const cellDate = currentWeekStart.clone().add(dayIndex, 'days').format('YYYY-MM-DD');

    return startOrderedEvents.map(({ event, idx }) => {
      const eventData = event?.extendedProps?.eventData;
      if (!eventData) return null;

      if (selectedStatusFilter) {
        const filterMap = { deadline: 'full', under: 'under', none: 'none' };
        if (getReservationStatus(eventData) !== filterMap[selectedStatusFilter]) return null;
      }

      const eventDate = eventData.classDate;
      if (eventDate !== cellDate) return null;

      // 반복수업은 courseClassId가 슬롯 간 동일하므로, 날짜+시간+idx를 포함해 슬롯별로 유일한 키 생성
      const eventKey = `${eventData.courseClassId || event.id || ''}-${eventDate}-${eventData.startTime}-${eventData.endTime}-${idx}`;

      const eventStartHour = timeToIndex(eventData.startTime);
      const eventEndHour = timeToIndex(eventData.endTime);

      const endTimeLimit = periodTimes?.endTime ? Number(periodTimes.endTime) : 24;
      const startTimeLimit = periodTimes?.startTime ? Number(periodTimes.startTime) : 0;

      const eventStartHourInt = Math.floor(eventStartHour);
      if (eventStartHourInt > endTimeLimit) {
        return null;
      }

      const clampedStart = clamp(eventStartHour, 0, 24);
      const clampedEnd = clamp(eventEndHour, 0, 24);

      if (hour >= startTimeLimit && hour < endTimeLimit + 1 && clampedStart <= hour + rowHours && clampedEnd > hour) {
        const isEventStartingInThisCell = clampedStart >= hour && clampedStart < hour + rowHours;
        if (!isEventStartingInThisCell) {
          return null;
        }

        const groupInfo = eventGroupMap.get(event);
        if (!groupInfo || groupInfo.column === -1) {
          return null;
        }

        // 시간 기반 높이와 위치 (행 길이 기준 퍼센트 — 1시간 행이면 기존과 같다)
        const eventHeight = `${((clampedEnd - clampedStart) / rowHours) * 100}%`;
        const topPercent = ((clampedStart - hour) / rowHours) * 100;
        const stackOffset = stackOffsets.get(event) || 0;
        const topOffset = stackOffset ? `calc(${topPercent}% + ${stackOffset}px)` : `${topPercent}%`;
        // 짧은 수업도 카드 최소 높이만큼은 확보한다
        const cardStyle = cardMinHeightPx > 0 ? { minHeight: `${cardMinHeightPx}px` } : undefined;

        const statusColor = getStatusColor(eventData);
        const fontBackgroundStatus = getStatusFontBackgroundColor(eventData);
        const fontStatus = getStatusFontColor(eventData);
        const statusLabel = getStatusLabel(eventData);

        // 이 이벤트와 실제로 시간이 겹치는 이벤트 수 계산
        let actualOverlapCount = 0;
        if (groupInfo.hasOverlap) {
          allEvents?.forEach(otherEvent => {
            if (otherEvent === event) return;
            const otherData = otherEvent?.extendedProps?.eventData;
            if (!otherData || otherData.classDate !== eventData.classDate) return;
            if (isTimeOverlapping(eventData.startTime, eventData.endTime, otherData.startTime, otherData.endTime)) {
              actualOverlapCount++;
            }
          });
        }

        let cardWidth, cardLeft;
        const totalColumns = groupInfo.hasOverlap ? Math.min(groupInfo.totalColumns, 10) : 1;

        if (actualOverlapCount > 0) {
          // 실제로 겹치는 이벤트가 있을 때만 나눠서 배치
          const widthPercent = 100 / totalColumns;
          cardWidth = `${widthPercent}%`;
          cardLeft = `${groupInfo.column * widthPercent}%`;
        } else {
          cardWidth = '100%';
          cardLeft = '0%';
        }

        // 겹침 없거나 1~2개: 가로 레이아웃, 3개 이상: 세로 레이아웃
        const useHorizontalLayout = actualOverlapCount === 0 || totalColumns <= 2;

        // 인원수 계산 (가로 레이아웃용)
        const currentCount = eventData.reservedMemberCount + eventData.attendedMemberCount;
        const countBadgeText = `${currentCount}/${eventData.maxMemberCount}`;

        // 개인 스케줄 패널 "수업으로 이동" 도착 대상 여부 (시안 4a 하이라이트)
        const isHighlighted =
          highlightedIds.length > 0 && highlightedIds.includes(String(eventData.courseClassTimeId));

        const tooltipContent = (
          <Box sx={{ p: 0 }}>
            <TooltipLessonHeader>
              <TooltipLessonTitle>{eventData.className}</TooltipLessonTitle>
              <TooltipStatusBadge badgeBg={fontBackgroundStatus} badgeFont={fontStatus}>
                {statusLabel}
              </TooltipStatusBadge>
            </TooltipLessonHeader>
            <TooltipBody>
              <TooltipInfoRow>
                <TooltipInfoLabel>시간</TooltipInfoLabel>
                <TooltipInfoValue>{eventData.startTime} - {eventData.endTime}</TooltipInfoValue>
              </TooltipInfoRow>
              <TooltipInfoRow>
                <TooltipInfoLabel>강사</TooltipInfoLabel>
                <TooltipInfoValue>{eventData.trainerName}</TooltipInfoValue>
              </TooltipInfoRow>
              {eventData.branchExerciseRoomName && (
                <TooltipInfoRow>
                  <TooltipInfoLabel>강의실</TooltipInfoLabel>
                  <TooltipInfoValue>{eventData.branchExerciseRoomName}</TooltipInfoValue>
                </TooltipInfoRow>
              )}
              <TooltipInfoRow>
                <TooltipInfoLabel>예약</TooltipInfoLabel>
                <TooltipInfoValue valueColor="#0F7F85">{currentCount} / {eventData.maxMemberCount}명</TooltipInfoValue>
              </TooltipInfoRow>
              <TooltipInfoRow>
                <TooltipInfoLabel>대기</TooltipInfoLabel>
                <TooltipInfoValue valueColor="#E67E22">{eventData.waitingMemberCount || 0}명 / {eventData.membersWaitMaxCount || 0}명</TooltipInfoValue>
              </TooltipInfoRow>
              <TooltipInfoRow>
                <TooltipInfoLabel>출석</TooltipInfoLabel>
                <TooltipInfoValue>{eventData.attendedMemberCount}명</TooltipInfoValue>
              </TooltipInfoRow>
              <TooltipInfoRow>
                <TooltipInfoLabel>결석</TooltipInfoLabel>
                <TooltipInfoValue>{eventData.absentedMemberCount}명</TooltipInfoValue>
              </TooltipInfoRow>
              {eventData.openDate && (
                <TooltipInfoRow>
                  <TooltipInfoLabel>자동공개</TooltipInfoLabel>
                  <TooltipInfoValue>{moment(eventData.openDate).format('M/D HH:mm')}</TooltipInfoValue>
                </TooltipInfoRow>
              )}
              {eventData.closeDate && (
                <TooltipInfoRow>
                  <TooltipInfoLabel>자동폐강</TooltipInfoLabel>
                  <TooltipInfoValue>{moment(eventData.closeDate).format('M/D HH:mm')} / {eventData.closeMemberCount ?? 0}명 이하</TooltipInfoValue>
                </TooltipInfoRow>
              )}
            </TooltipBody>
          </Box>
        );

        const tooltipProps = {
          arrow: true,
          placement: 'right',
          componentsProps: {
            popper: { sx: { pointerEvents: 'none' } },
            tooltip: {
              sx: {
                bgcolor: '#fff',
                color: '#333',
                borderRadius: '10px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                minWidth: '210px',
                p: '10px',
                border: '1px solid #E0E3E6',
                pointerEvents: 'none',
              },
            },
            arrow: {
              sx: {
                color: '#fff',
                '&::before': { border: '1px solid #E0E3E6' },
              },
            },
          },
        };

        // 가로 레이아웃 (1~3개)
        if (useHorizontalLayout) {
          return (
            <Tooltip
              key={eventKey}
              title={tooltipContent}
              {...tooltipProps}
              open={hoveredKey === eventKey && !isMenuOpen}
              disableHoverListener
              disableFocusListener
              disableTouchListener>
              <EventCardHorizontal
                className="event-card"
                data-class-time-id={eventData.courseClassTimeId}
                statusColor={statusColor}
                cardWidth={cardWidth}
                cardHeight={eventHeight}
                cardTop={topOffset}
                cardLeft={cardLeft}
                cardZIndex={10 + eventStartHourInt * 2 + groupInfo.column}
                isHighlighted={isHighlighted}
                style={cardStyle}
                onMouseDown={e => e.stopPropagation()}
                onMouseEnter={() => setHoveredKey(eventKey)}
                onMouseLeave={() => setHoveredKey(null)}
                onClick={e => {
                  e.stopPropagation();
                  setHoveredKey(null);
                  if (highlightedIds.length > 0) onClearHighlight?.();
                  onEventClick(e, event);
                }}
                onContextMenu={e => {
                  setHoveredKey(null);
                  onEventContextMenu?.(e, event);
                }}>
                {(() => {
                  const duration = calculateDuration(eventData.startTime, eventData.endTime);
                  const cardPixelHeight = Math.max(duration * cellHeight / 60, cardMinHeightPx);
                  const showDetails = cardPixelHeight >= 45;
                  return (
                    <EventCardHorizontalContent>
                      <EventCardHorizontalTitle>{formatClassLabel(eventData)}</EventCardHorizontalTitle>
                      {showDetails && (
                        <EventCardHorizontalInfo>
                          <EventCardHorizontalTimeRow>
                            <span>{eventData.trainerName} 강사</span>
                            <span>{eventData.startTime} ~ {eventData.endTime}</span>
                          </EventCardHorizontalTimeRow>
                          {eventData.courseName && (
                            <EventCardHorizontalCategory>{eventData.courseName}</EventCardHorizontalCategory>
                          )}
                        </EventCardHorizontalInfo>
                      )}
                    </EventCardHorizontalContent>
                  );
                })()}
                <EventCardHorizontalBadge bgColor={fontBackgroundStatus}>
                  <EventCardHorizontalBadgeText textColor={fontStatus}>{countBadgeText}</EventCardHorizontalBadgeText>
                </EventCardHorizontalBadge>
              </EventCardHorizontal>
            </Tooltip>
          );
        }

        // 세로 레이아웃 (4~5개)
        return (
          <Tooltip
            key={eventKey}
            title={tooltipContent}
            {...tooltipProps}
            open={hoveredKey === eventKey && !isMenuOpen}
            disableHoverListener
            disableFocusListener
            disableTouchListener>
            <EventCard
              className="event-card"
              data-class-time-id={eventData.courseClassTimeId}
              statusColor={statusColor}
              cardWidth={cardWidth}
              cardHeight={eventHeight}
              cardTop={topOffset}
              cardLeft={cardLeft}
              cardZIndex={10 + eventStartHourInt * 2 + groupInfo.column}
              isHighlighted={isHighlighted}
              style={cardStyle}
              onMouseDown={e => e.stopPropagation()}
              onMouseEnter={() => setHoveredKey(eventKey)}
              onMouseLeave={() => setHoveredKey(null)}
              onClick={e => {
                e.stopPropagation();
                setHoveredKey(null);
                if (highlightedIds.length > 0) onClearHighlight?.();
                onEventClick(e, event);
              }}
              onContextMenu={e => {
                setHoveredKey(null);
                onEventContextMenu?.(e, event);
              }}>
              {(() => {
                const duration = calculateDuration(eventData.startTime, eventData.endTime);
                const cardPixelHeight = Math.max(duration * cellHeight / 60, cardMinHeightPx);
                const showTrainer = cardPixelHeight >= 45;
                const showTime = cardPixelHeight >= 55;
                return (
                  <>
                    <EventCardHeader>
                      <EventCardBadge bgColor={fontBackgroundStatus}>
                        <EventCardBadgeText textColor={fontStatus}>
                          {countBadgeText}
                        </EventCardBadgeText>
                      </EventCardBadge>
                      <EventCardTitle>{formatClassLabel(eventData)}</EventCardTitle>
                    </EventCardHeader>
                    {showTrainer && <EventCardTrainer>{eventData.trainerName} 강사</EventCardTrainer>}
                    {showTime && <EventCardTime>{eventData.startTime} ~ {eventData.endTime}</EventCardTime>}
                  </>
                );
              })()}
            </EventCard>
          </Tooltip>
        );
      }
      return null;
    });
  };

  // 셀 클릭 핸들러 — 클릭한 선택 단위 구간의 시작 시각(분)으로 만든다
  const handleCellClick = (e, row, dayIndex) => {
    e.stopPropagation();
    if (hasDraggedRef.current) {
      hasDraggedRef.current = false;
      return;
    }
    onCellClick(snapStartAt(e, row), dayIndex);
  };

  return (
    <CalendarContainer
      ref={tableRef}
      isDragging={isDragging}
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      onMouseMove={handleMouseMove}>
      <CalendarTable>
        <thead>
          <tr>
            <TableHeaderCell isTimeColumn />
            {getWeekDates().map((date, idx) => {
              const isToday = date.isSame(moment(), 'day');
              return (
                <TableHeaderCell key={idx} isToday={isToday}>
                  <DayLabel isWeekend={date.day() === 0 || date.day() === 6} isSunday={date.day() === 0}>
                    {date.format('D')} ({mainLang === 'en' ? WEEKDAYS_EN[date.day()] : WEEKDAYS[date.day()]})
                    {isToday && <TodayBadge>today</TodayBadge>}
                  </DayLabel>
                </TableHeaderCell>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {hours.map(row => {
            // 행 높이는 행 길이에 비례(마지막 짧은 행 포함). 1시간 행이면 기존 cellHeight 그대로
            const rowHeight = (row.minutes / MINUTES_PER_HOUR) * cellHeight;
            return (
            <tr key={row.startMinutes}>
              <TimeCell cellHeight={rowHeight}>{row.label}</TimeCell>
              {getWeekDates().map((date, dayIdx) => {
                return (
                  <EventCell
                    key={dayIdx}
                    cellHeight={rowHeight}
                    onMouseMove={e => updateHoverBox(e, row)}
                    onClick={e => handleCellClick(e, row, dayIdx)}
                    onContextMenu={e => {
                      // 카드 위 우클릭은 카드가 복사로 처리한다.
                      if (e.target.closest('.event-card')) return;
                      onCellContextMenu?.(e, snapStartAt(e, row), dayIdx);
                    }}>
                    {/* 30분 보조선 — 1시간 칸에서만 */}
                    {timeUnitMinutes === MINUTES_PER_HOUR && <RowDivider />}
                    {getCurrentTimeIndicator(row, dayIdx)}
                    {renderEvents(row, dayIdx)}
                  </EventCell>
                );
              })}
            </tr>
            );
          })}
        </tbody>
      </CalendarTable>
    </CalendarContainer>
  );
};

export default GroupLessonCalendar;
